import { ProductsQuery } from '@generated/graphql/types';
import { ProductCardVertical } from '../Card/ProductCardVertical';
import { ButtonPrimary } from '@components/Button/Primary';
import { useMemo, useState } from 'react';
import useBreakpoint from '@hooks/useBreakpoint';
import { useTranslate } from '@tolgee/react';
import { useSoldProductZustand } from '@zustand/useSoldProduct';

type ProductListCustomGridProps = {
  products: ProductsQuery['products'] | { edges: { node: any }[] };
  onLoadMore: () => void;
  onProductClicked?: (handle: string) => void;
  canLoadMore: boolean;
  className?: string;
};

const ProductListCustomGrid = ({
  products,
  onLoadMore,
  canLoadMore,
  onProductClicked,
}: ProductListCustomGridProps) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isMobile = useBreakpoint();
  const { compare, skus } = useSoldProductZustand();

  const edges = useMemo(
    () => products.edges.sort((node1, node2) => compare(node1.node.sku, node2.node.sku)),
    [JSON.stringify(products.edges), skus.size],
  );

  const firstFour = {
    ...products,
    edges: edges.slice(0, 4),
  };
  const remaining = {
    ...products,
    edges: edges.slice(4),
  } as ProductsQuery['products'];
  const { t } = useTranslate();

  return (
    <>
      {isMobile ? (
        <>
          <div className={'grid mt-6 gap-y-6 gap-x-2 grid-cols-2'}>
            {firstFour.edges.slice(0, 2).map(
              (
                {
                  node: {
                    id,
                    title,
                    highlights,
                    handle,
                    priceRange,
                    collections,
                    // @ts-ignore
                    variantId,
                    sku,
                  },
                },
                i,
              ) => (
                <ProductCardVertical
                  collections={collections}
                  highlights={highlights?.value?.length ? JSON.parse(highlights.value) : []}
                  price={priceRange}
                  title={title}
                  handle={handle}
                  key={id}
                  onClick={onProductClicked}
                  variantSku={sku}
                  variantId={variantId}
                />
              ),
            )}

            {firstFour.edges.slice(2).map(
              (
                {
                  node: {
                    id,
                    title,
                    highlights,
                    handle,
                    priceRange,
                    collections,
                    // @ts-ignore
                    variantId,
                    sku,
                  },
                },
                i,
              ) => (
                <ProductCardVertical
                  collections={collections}
                  highlights={highlights?.value?.length ? JSON.parse(highlights.value) : []}
                  price={priceRange}
                  title={title}
                  handle={handle}
                  key={variantId}
                  onClick={onProductClicked}
                  variantSku={sku}
                  variantId={variantId}
                  className="col-span-2"
                />
              ),
            )}

            {remaining.edges.length > 0 &&
              remaining.edges.map(
                (
                  {
                    node: {
                      id,
                      title,
                      highlights,
                      handle,
                      priceRange,
                      collections,
                      // @ts-ignore
                      variantId,
                      sku,
                    },
                  },
                  i,
                ) => (
                  <ProductCardVertical
                    collections={collections}
                    highlights={highlights?.value?.length ? JSON.parse(highlights.value) : []}
                    price={priceRange}
                    title={title}
                    handle={handle}
                    key={variantId}
                    onClick={onProductClicked}
                    // @ts-ignore
                    variantSku={sku}
                    variantId={variantId}
                  />
                ),
              )}
          </div>
        </>
      ) : (
        <div className={'grid mt-6 gap-y-10 grid-cols-12'}>
          {firstFour.edges.map(
            (
              {
                node: {
                  id,
                  title,
                  highlights,
                  handle,
                  priceRange,
                  collections,
                  // @ts-ignore
                  variantId,
                  sku,
                },
              },
              i,
            ) => (
              <ProductCardVertical
                collections={collections}
                highlights={highlights?.value?.length ? JSON.parse(highlights.value) : []}
                price={priceRange}
                title={title}
                handle={handle}
                key={variantId}
                onClick={onProductClicked}
                // @ts-ignore
                variantSku={sku}
                variantId={variantId}
                className="col-span-3"
              />
            ),
          )}

          {remaining.edges.length > 0 &&
            remaining.edges.map(
              (
                {
                  node: {
                    id,
                    title,
                    highlights,
                    handle,
                    priceRange,
                    collections,
                    // @ts-ignore
                    variantId,
                    sku,
                  },
                },
                i,
              ) => (
                <ProductCardVertical
                  collections={collections}
                  highlights={highlights?.value?.length ? JSON.parse(highlights.value) : []}
                  price={priceRange}
                  title={title}
                  handle={handle}
                  key={variantId}
                  onClick={onProductClicked}
                  // @ts-ignore
                  variantSku={sku}
                  variantId={variantId}
                  className={`col-span-4 ${id}`}
                />
              ),
            )}
        </div>
      )}

      {canLoadMore && (
        <div className="flex justify-center my-5">
          <ButtonPrimary
            text={t('product.images.showMore')}
            loading={isLoadingMore}
            onClick={() => {
              setIsLoadingMore(true);
              onLoadMore();
              setIsLoadingMore(false);
            }}
          />
        </div>
      )}
    </>
  );
};

export default ProductListCustomGrid;
